import { useQueryClient } from 'react-query';
import { TaskCenterQueries, useTaskCenterMutations } from '@frontend/api-task-center';
import { useTranslation } from '@frontend/i18n';
import { ConfirmationModal, ConfirmationModalProps, useAlert, useModalControl } from '@frontend/design-system';
import { useTaskCenterStore } from '../provider';
import { taskEvents } from '../task-events';
import { taskCenterTrackingIds } from '../tracking-ids';

export interface UseDeleteTaskProps {
  id?: string;
}

export const useDeleteTask = ({ id }: UseDeleteTaskProps) => {
  const alert = useAlert();
  const queryClient = useQueryClient();
  const { t } = useTranslation('task-center');
  const { taskDeleteMutation } = useTaskCenterMutations();
  const { openModal, modalProps } = useModalControl();
  const { setTaskMode, setSelectedTask, setTaskId } = useTaskCenterStore([
    'setSelectedTask',
    'setTaskMode',
    'setTaskId',
  ]);

  const deleteTask = () => {
    if (!id) return;

    taskDeleteMutation.mutate(
      { id },
      {
        onError: () => {
          alert.error(t('Failed to delete task'));
        },
        onSuccess: (_, payload) => {
          taskEvents.emit('TASK_DELETED', { id: payload.id });
          setTaskId(null);
          setTaskMode(null);
          setSelectedTask(null);
          queryClient.invalidateQueries(TaskCenterQueries.queryEndpointKeys.tasksList);
        },
      }
    );
  };

  const triggerProps = {
    onClick: openModal,
    trackingId: taskCenterTrackingIds.deleteTaskFromDetails,
    loading: taskDeleteMutation.isLoading,
  };

  const confirmationModalProps: ConfirmationModalProps = {
    ...modalProps,
    title: t('Are you sure you want to delete the task?'),
    destructive: true,
    confirmTrackingId: taskCenterTrackingIds.deleteTaskConfirm,
    cancelTrackingId: taskCenterTrackingIds.deleteTaskCancel,
    onConfirm: deleteTask,
  };

  return {
    ConfirmationModal,
    modalProps: confirmationModalProps,
    triggerProps,
    isDeleting: taskDeleteMutation.isLoading,
  };
};
